/**
 * Step 4 (Supporting evidence) — service treatment records (STR), BDD only.
 *
 * Flow:
 *   1. Entry page   - "Do you want to upload your service treatment records?"
 *   2. Upload page  - List & Loop item page: one `va-file-input` plus a
 *                     decoupled record-type select (Medical/Dental)
 *   3. Review page  - List & Loop summary: uploaded files plus
 *                     "Do you have other service treatment records to add?"
 *
 * The loop's intro page is registered but never displayed (intro-bypass
 * pattern, see ./utils): "Yes" on the entry page goes straight into the loop.
 *
 * arrayBuilderPages requires the order intro, summary, then item pages.
 */
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { isBDD } from '../../utils/bdd';
import { prepareFilesAccordion } from '../../content/fileUpload';
import {
  STR_QUESTION_PATH,
  STR_UPLOAD_ITEM_PATH,
  STR_UPLOAD_SUMMARY_PATH,
  isUploadingStr,
  serviceTreatmentRecordsOptions,
  strUploadBypassedIntro,
} from './serviceTreatmentRecords/options';
import entryPage, {
  ServiceTreatmentRecordsRemovalPage,
} from './serviceTreatmentRecords/entry';
import introPage from './serviceTreatmentRecords/intro';
import uploadPage from './serviceTreatmentRecords/upload';
import summaryPage, {
  withLastDeleteRouting,
} from './serviceTreatmentRecords/summary';

const strUploadPages = arrayBuilderPages(
  serviceTreatmentRecordsOptions,
  pageBuilder => ({
    serviceTreatmentRecordsUploadIntro: pageBuilder.introPage({
      title: 'Upload your service treatment records',
      path: 'supporting-evidence/service-treatment-records-upload-intro',
      depends: isUploadingStr,
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
      // Intro-bypass pattern: never display the intro; redirect into the loop.
      ...strUploadBypassedIntro.introPage,
    }),
    serviceTreatmentRecordsUploadSummary: pageBuilder.summaryPage({
      title: 'Review your service treatment records',
      path: STR_UPLOAD_SUMMARY_PATH,
      depends: isUploadingStr,
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
      // Intro-bypass pattern: Back goes to the entry page.
      ...strUploadBypassedIntro.summaryPage,
    }),
    serviceTreatmentRecordsUpload: pageBuilder.itemPage({
      title: 'Upload your service treatment records',
      path: STR_UPLOAD_ITEM_PATH,
      depends: isUploadingStr,
      uiSchema: uploadPage.uiSchema,
      schema: uploadPage.schema,
      // Intro-bypass pattern: Back returns to the loop summary if items
      // remain, otherwise to the entry page.
      ...strUploadBypassedIntro.itemPage,
      // Rendered between the cancel button and the nav buttons.
      ContentBeforeButtons: () => prepareFilesAccordion,
    }),
  }),
);

// Deleting the last file returns to the entry page (see ./summary).
strUploadPages.serviceTreatmentRecordsUploadSummary.CustomPage =
  withLastDeleteRouting(
    strUploadPages.serviceTreatmentRecordsUploadSummary.CustomPage,
  );

export const serviceTreatmentRecordsPages = {
  serviceTreatmentRecords: {
    title: 'Service treatment records',
    path: STR_QUESTION_PATH,
    depends: formData => isBDD(formData),
    // Intercepts Continue with a destructive confirmation modal when "No" is
    // selected and files were already uploaded.
    CustomPage: ServiceTreatmentRecordsRemovalPage,
    CustomPageReview: null,
    uiSchema: entryPage.uiSchema,
    schema: entryPage.schema,
    // "Yes" goes straight to the first upload (or the review page if files
    // already exist), skipping the loop's intro page.
    ...strUploadBypassedIntro.decisionPage(isUploadingStr),
  },
  ...strUploadPages,
};

export default serviceTreatmentRecordsPages;
